import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from "react";
import { roomCalendarService, roomSocketService } from "../services/rooms";
import { toast } from "react-toastify";

const RoomCalendarContext = createContext();

export const useRoomCalendar = () => {
  const context = useContext(RoomCalendarContext);
  if (!context) {
    throw new Error(
      "useRoomCalendar must be used within a RoomCalendarProvider"
    );
  }
  return context;
};

export const RoomCalendarProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentRoomId, setCurrentRoomId] = useState(null);

  /**
   * Fetch events for a room with optional filters
   */
  const fetchEvents = useCallback(async (roomId, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomCalendarService.getEvents(roomId, options);

      if (response.success) {
        if (options.upcoming) {
          setUpcomingEvents(response.data || []);
        } else {
          setEvents(response.data || []);
        }
        return response;
      }
    } catch (err) {
setError(err.message);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new event
   */
  const createEvent = useCallback(
    async (roomId, eventData) => {
      try {
        setLoading(true);
        setError(null);
        const response = await roomCalendarService.createEvent(
          roomId,
          eventData
        );

        if (response.success) {
          toast.success("Event created successfully!");

          // Notify via socket
          if (currentRoomId) {
            roomSocketService.notifyEventCreated(currentRoomId, response.data);
          }

          // Refresh events
          if (currentRoomId) {
            await fetchEvents(currentRoomId);
          }

          return response.data;
        }
      } catch (err) {
setError(err.message);
        toast.error(err.message || "Failed to create event");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentRoomId, fetchEvents]
  );

  /**
   * Update an event
   */
  const updateEvent = useCallback(
    async (roomId, eventId, updates) => {
      try {
        setLoading(true);
        setError(null);
        const response = await roomCalendarService.updateEvent(
          roomId,
          eventId,
          updates
        );

        if (response.success) {
          toast.success("Event updated successfully!");

          // Notify via socket
          if (currentRoomId) {
            roomSocketService.notifyEventUpdated(
              currentRoomId,
              eventId,
              updates
            );
          }

          // Update local state
          setEvents((prev) =>
            prev.map((event) =>
              event._id === eventId ? { ...event, ...response.data } : event
            )
          );

          return response.data;
        }
      } catch (err) {
setError(err.message);
        toast.error(err.message || "Failed to update event");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentRoomId]
  );

  /**
   * Delete/cancel an event
   */
  const deleteEvent = useCallback(async (roomId, eventId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await roomCalendarService.deleteEvent(roomId, eventId);

      if (response.success) {
        toast.success("Event cancelled successfully!");

        // Remove from local state
        setEvents((prev) => prev.filter((event) => event._id !== eventId));
        setUpcomingEvents((prev) =>
          prev.filter((event) => event._id !== eventId)
        );

        return response;
      }
    } catch (err) {
setError(err.message);
      toast.error(err.message || "Failed to cancel event");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update RSVP status for an event
   */
  const updateRSVP = useCallback(
    async (roomId, eventId, rsvp) => {
      try {
        const response = await roomCalendarService.updateRSVP(
          roomId,
          eventId,
          rsvp
        );

        if (response.success) {
          toast.success(`RSVP updated to: ${rsvp}`);

          // Notify via socket
          if (currentRoomId) {
            roomSocketService.notifyRSVPUpdated(currentRoomId, eventId, rsvp);
          }

          // Update local state optimistically
          const updateEventRSVP = (event) => {
            if (event._id === eventId && response.data) {
              return {
                ...event,
                acceptedCount: response.data.acceptedCount,
                declinedCount: response.data.declinedCount,
              };
            }
            return event;
          };

          setEvents((prev) => prev.map(updateEventRSVP));
          setUpcomingEvents((prev) => prev.map(updateEventRSVP));

          return response.data;
        }
      } catch (err) {
toast.error(err.message || "Failed to update RSVP");
        throw err;
      }
    },
    [currentRoomId]
  );

  /**
   * Socket event handlers
   */
  useEffect(() => {
    if (!currentRoomId) return;

    const handleEventCreated = (data) => {
      if (data.roomId === currentRoomId) {
fetchEvents(currentRoomId);
      }
    };

    const handleEventUpdated = (data) => {
      if (data.roomId === currentRoomId) {
fetchEvents(currentRoomId);
      }
    };

    const handleRSVPUpdated = (data) => {
      if (data.roomId === currentRoomId) {
fetchEvents(currentRoomId);
      }
    };

    const handleEventCancelled = (data) => {
      if (data.roomId === currentRoomId) {
setEvents((prev) => prev.filter((event) => event._id !== data.eventId));
        setUpcomingEvents((prev) =>
          prev.filter((event) => event._id !== data.eventId)
        );
      }
    };

    // Subscribe to socket events
    const unsubCreated = roomSocketService.on(
      "new_event_created",
      handleEventCreated
    );
    const unsubUpdated = roomSocketService.on(
      "event_updated",
      handleEventUpdated
    );
    const unsubRSVP = roomSocketService.on(
      "event_rsvp_updated",
      handleRSVPUpdated
    );
    const unsubCancelled = roomSocketService.on(
      "event_cancelled",
      handleEventCancelled
    );

    // Cleanup
    return () => {
      unsubCreated();
      unsubUpdated();
      unsubRSVP();
      unsubCancelled();
    };
  }, [currentRoomId, fetchEvents]);

  const value = {
    events,
    upcomingEvents,
    loading,
    error,
    currentRoomId,
    setCurrentRoomId,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    updateRSVP,
  };

  return (
    <RoomCalendarContext.Provider value={value}>
      {children}
    </RoomCalendarContext.Provider>
  );
};

export default RoomCalendarContext;
